<template>
  <div class="agreement-page">
    <div class="agreement-header">
      <button class="back-btn" @click="handleBack" aria-label="Back">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>
      <h1 class="header-title">Agreement</h1>
      <div class="header-placeholder"></div>
    </div>

    <div class="tab-bar">
      <button
        class="tab-item"
        :class="{ active: activeTab === 'user' }"
        @click="switchTab('user')"
      >
        User Agreement
      </button>
      <button
        class="tab-item"
        :class="{ active: activeTab === 'privacy' }"
        @click="switchTab('privacy')"
      >
        Privacy Agreement
      </button>
    </div>

    <div class="tab-indicator">
      <div class="indicator" :class="activeTab"></div>
    </div>

    <div class="agreement-content">
      <div v-if="loading" class="state-wrapper">
        <div class="spinner"></div>
        <p class="state-text">Loading...</p>
      </div>

      <div v-else-if="error" class="state-wrapper">
        <p class="state-text error-text">{{ error }}</p>
        <button class="retry-btn" @click="fetchAgreement">Retry</button>
      </div>

      <div v-else class="content-wrapper" v-html="content"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AgreementPage',
  data() {
    return {
      activeTab: 'user',
      title: '',
      content: '',
      loading: false,
      error: ''
    }
  },
  created() {
    this.fetchAgreement()
  },
  methods: {
    switchTab(tab) {
      if (tab === this.activeTab) return
      this.activeTab = tab
      this.title = ''
      this.content = ''
      this.error = ''
      this.fetchAgreement()
    },
    async fetchAgreement() {
      this.loading = true
      this.error = ''
      try {
        const params = new URLSearchParams({
          type: this.activeTab,
          lang: 'en'
        })
        const res = await fetch(
          `https://ai-reader.com.cn/api/user/agreementItem?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          }
        )
        if (!res.ok) {
          throw new Error(`Request failed with status ${res.status}`)
        }
        const json = await res.json()
        if (json.code === 1 && json.data) {
          this.title = json.data.title || ''
          this.content = json.data.content || ''
        } else {
          throw new Error(json.msg || 'Failed to load agreement')
        }
      } catch (err) {
        this.error = err.message || 'Failed to load agreement'
      } finally {
        this.loading = false
      }
    },
    handleBack() {
      if (window.history.length > 1) {
        window.history.back()
      }
    }
  }
}
</script>

<style scoped>
.agreement-page {
  min-height: 100vh;
  background-color: #f7f7f7;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.agreement-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: #ffffff;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  z-index: 10;
}

.back-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #333;
  cursor: pointer;
  border-radius: 50%;
}

.back-btn:active {
  background-color: #f0f0f0;
}

.header-title {
  font-size: 17px;
  font-weight: 600;
  color: #222;
  margin: 0;
}

.header-placeholder {
  width: 40px;
  height: 40px;
}

.tab-bar {
  display: flex;
  background-color: #ffffff;
}

.tab-item {
  flex: 1;
  padding: 14px 0;
  background: none;
  border: none;
  font-size: 15px;
  color: #666;
  cursor: pointer;
  transition: color 0.2s;
  position: relative;
}

.tab-item.active {
  color: #2b7fff;
  font-weight: 600;
}

.tab-indicator {
  height: 2px;
  background-color: #ffffff;
  position: relative;
}

.indicator {
  position: absolute;
  top: 0;
  height: 2px;
  width: 50%;
  background-color: #2b7fff;
  transition: transform 0.25s ease;
}

.indicator.user {
  transform: translateX(0);
}

.indicator.privacy {
  transform: translateX(100%);
}

.agreement-content {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
}

.state-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #999;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e0e0e0;
  border-top-color: #2b7fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.state-text {
  margin-top: 12px;
  font-size: 14px;
}

.error-text {
  color: #e74c3c;
}

.retry-btn {
  margin-top: 16px;
  padding: 8px 24px;
  background-color: #2b7fff;
  color: #fff;
  border: none;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
}

.retry-btn:active {
  opacity: 0.85;
}

.content-wrapper {
  background-color: #ffffff;
  padding: 20px;
  border-radius: 8px;
  line-height: 1.7;
  color: #333;
  font-size: 14px;
  word-break: break-word;
}

.content-wrapper :deep(h1) {
  font-size: 20px;
  font-weight: 700;
  margin: 20px 0 12px;
  color: #222;
}

.content-wrapper :deep(h2) {
  font-size: 17px;
  font-weight: 700;
  margin: 24px 0 10px;
  color: #222;
}

.content-wrapper :deep(h3) {
  font-size: 15px;
  font-weight: 600;
  margin: 20px 0 8px;
  color: #333;
}

.content-wrapper :deep(p) {
  margin: 10px 0;
}

.content-wrapper :deep(em) {
  font-style: italic;
}

.content-wrapper :deep(a) {
  color: #2b7fff;
  text-decoration: none;
}

/* ===== Responsive: Tablet (>=768px) ===== */
@media screen and (min-width: 768px) {
  .agreement-page {
    max-width: 720px;
    margin: 0 auto;
    box-shadow: 0 0 24px rgba(0, 0, 0, 0.06);
  }

  .agreement-header {
    padding: 14px 24px;
  }

  .header-title {
    font-size: 18px;
  }

  .tab-item {
    padding: 16px 0;
    font-size: 16px;
  }

  .agreement-content {
    padding: 24px;
  }

  .content-wrapper {
    padding: 28px;
    font-size: 15px;
    line-height: 1.8;
  }

  .content-wrapper :deep(h1) {
    font-size: 24px;
  }

  .content-wrapper :deep(h2) {
    font-size: 19px;
  }

  .content-wrapper :deep(h3) {
    font-size: 16px;
  }
}

/* ===== Responsive: Desktop (>=1024px) ===== */
@media screen and (min-width: 1024px) {
  .agreement-page {
    max-width: 820px;
  }

  .agreement-header {
    padding: 16px 32px;
  }

  .agreement-content {
    padding: 32px;
  }

  .content-wrapper {
    padding: 36px 40px;
    font-size: 16px;
    line-height: 1.85;
  }

  .content-wrapper :deep(h1) {
    font-size: 26px;
  }

  .content-wrapper :deep(h2) {
    font-size: 21px;
  }

  .content-wrapper :deep(h3) {
    font-size: 17px;
  }
}

/* ===== Responsive: Large desktop (>=1440px) ===== */
@media screen and (min-width: 1440px) {
  .agreement-page {
    max-width: 900px;
  }
}
</style>
